/**
 * Разметка юридических текстов.
 *
 * Собирается в React-элементы, а не в HTML-строку: текст приходит из базы,
 * и `dangerouslySetInnerHTML` превратил бы правку документа в админке
 * в возможность выполнить скрипт у каждого, кто его откроет.
 *
 * Поддерживается ровно то, что используется в документах: заголовки двух
 * уровней, абзацы, списки и жирный шрифт. Полноценный markdown здесь был бы
 * лишней зависимостью ради возможностей, которых в текстах нет.
 */
import type { JSX, ReactNode } from 'react';

/** `**жирный**` внутри строки. */
function inline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`${keyPrefix}-${index}`} className="font-semibold text-content">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

export function Markdown({ source }: { source: string }): JSX.Element {
  const blocks: ReactNode[] = [];
  const lines = source.split('\n');

  let list: string[] = [];
  let paragraph: string[] = [];

  const flushList = (): void => {
    if (list.length === 0) return;
    const items = list;
    blocks.push(
      <ul key={`list-${blocks.length}`} className="flex list-disc flex-col gap-1 pl-5">
        {items.map((item, index) => (
          <li key={index}>{inline(item, `li-${blocks.length}-${index}`)}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  const flushParagraph = (): void => {
    if (paragraph.length === 0) return;
    // Переносы внутри абзаца — вёрстка исходника, а не смысл: склеиваем.
    const text = paragraph.join(' ');
    blocks.push(<p key={`p-${blocks.length}`}>{inline(text, `p-${blocks.length}`)}</p>);
    paragraph = [];
  };

  for (const raw of lines) {
    const line = raw.trim();

    if (line === '') {
      flushParagraph();
      flushList();
      continue;
    }

    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h2 key={`h2-${blocks.length}`} className="mt-4 text-title-3 text-content">
          {line.slice(3)}
        </h2>,
      );
      continue;
    }

    if (line.startsWith('# ')) {
      flushParagraph();
      flushList();
      blocks.push(
        <h1 key={`h1-${blocks.length}`} className="text-title-2 text-content">
          {line.slice(2)}
        </h1>,
      );
      continue;
    }

    if (line.startsWith('- ')) {
      flushParagraph();
      list.push(line.slice(2));
      continue;
    }

    paragraph.push(line);
  }

  flushParagraph();
  flushList();

  return <div className="flex flex-col gap-3">{blocks}</div>;
}
